import React, { useEffect, useRef, useState } from 'react';
import useAxios from 'axios-hooks';
import { PrimaryButton, Toggle, ChoiceGroup, Dropdown, MessageBar, MessageBarType } from '@fluentui/react';
import { Form, Formik } from 'formik';
import { useParams, useHistory } from 'react-router-dom';
import Select from 'react-select';
import { FixedSizeList as List } from 'react-window';
import TextField from '../../../Forms/TextField';
import UnitDropdown from '../../../DomainShared/UnitDropdown';
import Toast from '../../../Forms/Toast';
import FeatureSummary from '../../../Util/FeatureSummary';

const newCustomTaskObject = {
    cobrandId: null,
    cobrandTagId: null,
    unitIds: [],
    slug: '',
    category: '',
    title: '',
    description: '',
    url: '',
    color: 'Q6',
    priority: 10,
    superPriority: false,
    points: 100,
    hasCompletionCheck: false,
    completionType: null,
    completionTargetSlug: '',
    completionCobrandSlug: '',
    completionInteractionType: null,
    completionInteractionProperty: null,
};

const colorOptions = [
    { key: 'Q6', text: 'Q6 (Blue)' },
    { key: 'Q2', text: 'Q2 (Green)' },
];

const completionTypeOptions = [
    { key: 'ChecklistIsComplete', text: 'Checklist Completion' },
    { key: 'InteractionCheckIsComplete', text: 'Interaction Check' },
];

// Allowed interaction types with their properties
const interactionTypeDefinitions = {
    ViewAdvisors: [],
    ViewTopics: [],
    ViewSubTopic: ['SubTopicSlug', 'SubTopicName'],
    ViewArticle: ['ArticleSlug', 'ArticleName', 'ConTypeSlug'],
    ViewArticleInfographic: ['ArticleSlug', 'ArticleName'],
    ViewEmbeddedInfographic: ['InfographicName', 'InforgraphicUrl'],
    ViewCalculator: ['CalculatorSlug', 'CalculatorName'],
    StartVideo: ['VideoSlug', 'VideoName'],
    CompleteVideo: ['VideoSlug', 'VideoName'],
    ViewCourses: [],
    StartCourse: ['CourseSlug'],
    CompleteCourse: ['CourseSlug'],
    StartAssessment: [],
    CompleteAssessment: [],
    ViewTools: [],
    StartInteractiveExercise: ['InteractiveExerciseSlug'],
    CompleteInteractiveExercise: ['InteractiveExerciseSlug'],
    CreateResume: [],
    CreateWebinarRegistration: ['WebinarId', 'WebinarTitle'],
    ViewWebinar: ['WebinarId', 'WebinarTitle'],
    StartCreditScore: [],
    CompleteCreditScore: [],
    ViewCreditScore: [],
    CreateBudget: [],
    StartMeditation: ['MeditationSlug', 'MeditationTitle'],
    CompleteMeditation: ['MeditationSlug', 'MeditationTitle'],
    CreateJournal: ['JournalId', 'PromptId'],
    CreateStressScore: ['Score'],
    ViewMindfulness: [],
    StartMindfulnessProgramDay: ['DayNumber', 'WeekNumber'],
    CompleteMindfulnessProgramDay: ['DayNumber', 'WeekNumber'],
};

const interactionTypeOptions = Object.keys(interactionTypeDefinitions).map(key => ({
    key: key,
    text: key,
}));

const targetTypeOptions = [
    { key: 'cobrand', text: 'Cobrand' },
    { key: 'cobrandTag', text: 'Cobrand Tag' },
];

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        padding: '0 8px',
                        boxSizing: 'border-box',
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const CDN_BASE_URL = 'https://cdn.igrad.com/images/dashboard/icons';

const WizardPage = () => {
    const [showToast, setShowToast] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [targetType, setTargetType] = useState('cobrand');
    const [iconPreview, setIconPreview] = useState(null);
    const [iconError, setIconError] = useState('');
    const [cdnImageExists, setCdnImageExists] = useState(false);
    const hiddenFileInputRef = useRef(null);

    const { customTaskId: idFromParams } = useParams();
    const history = useHistory();

    const [{ data: customTask }, getCustomTask] = useAxios(
        { url: `/resources/custom-dashboard-tasks/${idFromParams}`, method: 'GET' },
        { manual: true }
    );

    useEffect(() => {
        if (idFromParams) {
            getCustomTask();
        }
    }, [idFromParams]);

    useEffect(() => {
        if (customTask) {
            if (customTask.cobrandTagId) {
                setTargetType('cobrandTag');
            } else {
                setTargetType('cobrand');
            }
            // Check if CDN image exists for this task
            if (customTask.slug) {
                const img = new Image();
                img.onload = () => setCdnImageExists(true);
                img.onerror = () => setCdnImageExists(false);
                img.src = `${CDN_BASE_URL}/icon-${customTask.slug}.png`;
            }
        }
    }, [customTask]);

    const [{ data: cobrands, loading: loadingCobrands }] = useAxios('/internal/cobrands');
    const cobrandsOptions = cobrands && cobrands.map(u => ({ value: u.cobrandId, label: u.name }));

    const [{ data: cobrandTags, loading: loadingCobrandTags }] = useAxios('/internal/tags');
    const cobrandTagsOptions = cobrandTags && cobrandTags.map(t => ({ value: t.coBrandTagID, label: t.name }));

    const [{ loading: submitting }, submitForm] = useAxios(
        { url: `/resources/custom-dashboard-tasks` },
        { manual: true }
    );

    const [{ loading: uploadingIcon }, uploadIcon] = useAxios(
        {
            url: '/resources/content/admin/upload-image',
            method: 'post',
            headers: { 'content-type': 'multipart/form-data' }
        },
        { manual: true }
    );

    const handleIconClick = () => {
        hiddenFileInputRef.current?.click();
    };

    const handleIconChange = async (e, slug) => {
        if (!e.target.files) return;
        const file = e.target.files[0];

        if (!slug) {
            setIconError('Please enter a slug first');
            return;
        }

        if (file.type !== 'image/png') {
            setIconError('Please upload a .png file');
            return;
        }

        const img = new Image();
        const objectUrl = window.URL.createObjectURL(file);

        img.onload = async () => {
            if (img.width < 150 || img.height < 150) {
                setIconError('Image dimensions must be at least 150x150');
                window.URL.revokeObjectURL(objectUrl);
                return;
            }

            setIconError('');
            setIconPreview(objectUrl);
            setCdnImageExists(false); // Hide CDN preview while uploading new

            const dashboardFileName = `${slug}.png`;
            const iconsFileName = `icon-${slug}.png`;

            try {
                // Upload to /dashboard folder (no icon- prefix)
                const dashboardFile = new File([file], dashboardFileName, { type: file.type });
                const data1 = new FormData();
                data1.append('Image', dashboardFile);
                data1.append('Path', 'dashboard');
                data1.append('Container', 'images');
                await uploadIcon({ data: data1 });

                // Upload to /dashboard/icons folder (with icon- prefix)
                const iconsFile = new File([file], iconsFileName, { type: file.type });
                const data2 = new FormData();
                data2.append('Image', iconsFile);
                data2.append('Path', 'dashboard/icons');
                data2.append('Container', 'images');
                await uploadIcon({ data: data2 });

                setCdnImageExists(true);
            } catch (err) {
                setIconError('Failed to upload icon');
            }
        };

        img.src = objectUrl;
    };

    const initialValues = customTask
        ? {
            ...newCustomTaskObject,
            ...customTask,
            unitIds: customTask.unitIds || [],
        }
        : newCustomTaskObject;

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values) => {
                setShowError(false);
                setErrorMessage('');

                const { unitIds, ...restValues } = values;
                const payload = {
                    ...restValues,
                    cobrandId: targetType === 'cobrand' ? values.cobrandId : null,
                    cobrandTagId: targetType === 'cobrandTag' ? values.cobrandTagId : null,
                    unitIds: targetType === 'cobrand' && values.cobrandId
                        ? unitIds || []
                        : [],
                };

                if (!values.hasCompletionCheck) {
                    payload.completionType = null;
                    payload.completionTargetSlug = '';
                    payload.completionCobrandSlug = '';
                    payload.completionInteractionType = null;
                    payload.completionInteractionProperty = null;
                }

                try {
                    let result = await submitForm({
                        url: idFromParams 
                            ? `/resources/custom-dashboard-tasks/${idFromParams}` 
                            : '/resources/custom-dashboard-tasks',
                        method: idFromParams ? 'PUT' : 'POST',
                        data: payload,
                    });

                    if (!result.isAxiosError) {
                        if (!idFromParams) {
                            history.push(`/dashboard-feature/custom-tasks/${result.data.customTaskId}/wizard`);
                        }
                        setShowToast(true);
                    }
                } catch (err) {
                    setShowError(true);
                    setErrorMessage(err.response?.data?.message || 'An error occurred');
                }
            }}
            validate={(values) => {
                const errors = {};
                if (!values.slug) errors.slug = 'Slug is required';
                if (!values.title) errors.title = 'Title is required';
                if (!values.description) errors.description = 'Description is required';
                if (!values.url) errors.url = 'URL is required';
                if (!values.category) errors.category = 'Category is required';
                if (targetType === 'cobrand' && !values.cobrandId) errors.cobrandId = 'Cobrand is required';
                if (targetType === 'cobrandTag' && !values.cobrandTagId) errors.cobrandTagId = 'Cobrand Tag is required';
                if (values.hasCompletionCheck) {
                    if (!values.completionType) errors.completionType = 'Completion type is required';
                    if (values.completionType === 'ChecklistIsComplete') {
                        if (!values.completionTargetSlug) errors.completionTargetSlug = 'Checklist slug is required';
                    }
                    if (values.completionType === 'InteractionCheckIsComplete') {
                        if (!values.completionInteractionType) errors.completionInteractionType = 'Interaction type is required';
                    }
                }
                return errors;
            }}
            validateOnBlur
            enableReinitialize={true}
        >
            {({ errors, values, setFieldValue, handleSubmit }) => {
                return (
                    <Form style={{ maxWidth: '600px', marginBottom: 100 }}>
                        <h4>Custom Dashboard Task</h4>
                        {showError && (
                            <MessageBar messageBarType={MessageBarType.error} className="mb10">
                                {errorMessage}
                            </MessageBar>
                        )}

                        {/* Basic Info */}
                        <div className="mt20">
                            <TextField
                                label="Slug"
                                name="slug"
                                required
                                errorMessage={errors.slug}
                                disabled={!!idFromParams}
                                description="Must start with 'getting-started-' (e.g., getting-started-my-task)"
                            />
                            <TextField
                                label="Title"
                                name="title"
                                className="mt10"
                                required
                                errorMessage={errors.title}
                            />
                            <TextField
                                label="Description"
                                name="description"
                                className="mt10"
                                multiline
                                rows={3}
                                required
                                errorMessage={errors.description}
                            />
                            <TextField
                                label="URL"
                                name="url"
                                className="mt10"
                                required
                                errorMessage={errors.url}
                                placeholder="/list/my-checklist"
                            />
                            
                                        <div style={{ fontStyle: 'italic', color: '#666', fontSize: 12 }}>
                                            The url to the task to complete
                                        </div>
                            <TextField
                                label="Category"
                                name="category"
                                className="mt10"
                                required
                                errorMessage={errors.category}
                                placeholder="getting-started"
                            />
                        </div>

                        {/* Target Selection */}
                        <div className="mt20">
                            <label className="default-label">Target Type</label>
                            <ChoiceGroup
                                selectedKey={targetType}
                                options={targetTypeOptions}
                                onChange={(e, option) => {
                                    setTargetType(option.key);
                                    if (option.key === 'cobrand') {
                                        setFieldValue('cobrandTagId', null);
                                    } else {
                                        setFieldValue('cobrandId', null);
                                        setFieldValue('unitIds', []);
                                    }
                                }}
                            />
                        </div>

                        {targetType === 'cobrand' && (
                            <div className="mt10">
                                <label className="default-label">Select Cobrand *</label>
                                <Select
                                    components={{ MenuList: CustomMenuList }}
                                    options={cobrandsOptions}
                                    placeholder="Select Cobrand"
                                    name="cobrandId"
                                    value={cobrandsOptions?.find(
                                        (option) => option.value === values.cobrandId
                                    )}
                                    onChange={(option) => {
                                        const nextCobrandId = option?.value || null;
                                        if (nextCobrandId !== values.cobrandId) {
                                            setFieldValue('unitIds', []);
                                        }
                                        setFieldValue('cobrandId', nextCobrandId);
                                    }}
                                    isSearchable
                                    isClearable
                                    isLoading={loadingCobrands}
                                    styles={{
                                        container: (base) => ({ ...base, width: 400 }),
                                        control: (base) => ({ ...base, border: '1px solid #aaaaaa' }),
                                    }}
                                />
                                {errors.cobrandId && (
                                    <span style={{ color: 'rgb(164, 38, 44)', fontSize: 12 }}>{errors.cobrandId}</span>
                                )}
                            </div>
                        )}

                        {targetType === 'cobrandTag' && (
                            <div className="mt10">
                                <label className="default-label">Select Cobrand Tag *</label>
                                <Select
                                    components={{ MenuList: CustomMenuList }}
                                    options={cobrandTagsOptions}
                                    placeholder="Select Cobrand Tag"
                                    name="cobrandTagId"
                                    value={cobrandTagsOptions?.find(
                                        (option) => option.value === values.cobrandTagId
                                    )}
                                    onChange={(option) => {
                                        setFieldValue('cobrandTagId', option?.value || null);
                                    }}
                                    isSearchable
                                    isClearable
                                    isLoading={loadingCobrandTags}
                                    styles={{
                                        container: (base) => ({ ...base, width: 400 }),
                                        control: (base) => ({ ...base, border: '1px solid #aaaaaa' }),
                                    }}
                                />
                                {errors.cobrandTagId && (
                                    <span style={{ color: 'rgb(164, 38, 44)', fontSize: 12 }}>{errors.cobrandTagId}</span>
                                )}
                            </div>
                        )}

                        {targetType === 'cobrand' && (
                            <div className="mt20">
                                <label className="default-label">Reporting Units</label>
                                <div style={{ fontSize: 12, marginBottom: 5 }}>
                                    Leave empty to show this task for all units in the selected cobrand.
                                </div>
                                {values.cobrandId && (!values.unitIds || values.unitIds.length === 0) && (
                                    <MessageBar messageBarType={MessageBarType.warning} className="mb10">
                                        No reporting units are selected. Saving now will show this task to every unit in the selected cobrand.
                                    </MessageBar>
                                )}
                                <UnitDropdown
                                    name="unitIds"
                                    cobrandId={values.cobrandId}
                                    placeholder={values.cobrandId ? 'Select units (empty = all)' : 'Select a cobrand first'}
                                    isDisabled={!values.cobrandId}
                                    description={
                                        values.cobrandId
                                            ? 'If units are selected, only members in those units will see this task.'
                                            : 'Select a cobrand first to limit this task to specific units.'
                                    }
                                />
                            </div>
                        )}

                        {/* Display Settings */}
                        <div className="mt20">
                            <Dropdown
                                label="Color"
                                selectedKey={values.color}
                                options={colorOptions}
                                onChange={(e, option) => setFieldValue('color', option.key)}
                                styles={{ dropdown: { width: 200 } }}
                            />
                            <TextField
                                label="Priority"
                                name="priority"
                                className="mt10"
                                type="number"
                                styles={{ fieldGroup: { width: 100 } }}
                            />
                            <TextField
                                label="Points"
                                name="points"
                                className="mt10"
                                type="number"
                                styles={{ fieldGroup: { width: 100 } }}
                            />
                            <div className="mt10">
                                <Toggle
                                    label="Super Priority"
                                    checked={values.superPriority}
                                    onChange={(e, checked) => setFieldValue('superPriority', checked)}
                                />
                            </div>
                        </div>

                        {/* Icon Upload */}
                        <div className="mt20">
                            <label className="default-label">Task Icon (150x150 min, .png only)</label>
                            {iconError && (
                                <div style={{ color: 'red', marginBottom: 10 }}>{iconError}</div>
                            )}
                            {/* Show existing CDN image when editing */}
                            {cdnImageExists && values.slug && !iconPreview && (
                                <div className="mb10">
                                    <img 
                                        src={`${CDN_BASE_URL}/icon-${values.slug}.png?t=${Date.now()}`} 
                                        alt="Current icon" 
                                        style={{ maxWidth: 64, maxHeight: 64 }} 
                                    />
                                    <div style={{ fontSize: 12, color: '#666' }}>Current icon</div>
                                </div>
                            )}
                            {/* Show preview of newly uploaded icon */}
                            {iconPreview && (
                                <div className="mb10">
                                    <img src={iconPreview} alt="Icon preview" style={{ maxWidth: 64, maxHeight: 64 }} />
                                    <div style={{ fontSize: 12, color: '#666' }}>New icon preview</div>
                                </div>
                            )}
                            <PrimaryButton
                                onClick={handleIconClick}
                                text="Upload Icon"
                                disabled={uploadingIcon || !values.slug}
                            />
                            <input
                                ref={hiddenFileInputRef}
                                type="file"
                                accept=".png"
                                style={{ display: 'none' }}
                                onChange={(e) => handleIconChange(e, values.slug)}
                            />
                            {!values.slug && (
                                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                    Enter a slug first to enable icon upload
                                </div>
                            )}
                        </div>

                        {/* Completion Check */}
                        <div className="mt20">
                            <Toggle
                                label="Has Completion Check"
                                checked={values.hasCompletionCheck}
                                onChange={(e, checked) => setFieldValue('hasCompletionCheck', checked)}
                            />
                        </div>

                        {values.hasCompletionCheck && (
                            <div className="mt10" style={{ marginLeft: 20, padding: 10, border: '1px solid #eee', borderRadius: 4 }}>
                                <Dropdown
                                    label="Completion Type"
                                    selectedKey={values.completionType}
                                    options={completionTypeOptions}
                                    onChange={(e, option) => setFieldValue('completionType', option.key)}
                                    styles={{ dropdown: { width: 300 } }}
                                    required
                                    errorMessage={errors.completionType}
                                />

                                {values.completionType === 'ChecklistIsComplete' && (
                                    <>
                                        <TextField
                                            label="Checklist Slug"
                                            name="completionTargetSlug"
                                            className="mt10"
                                            required
                                            errorMessage={errors.completionTargetSlug}
                                            placeholder="pillar-plan"
                                        />
                                    </>
                                )}

                                {values.completionType === 'InteractionCheckIsComplete' && (
                                    <>
                                        <Dropdown
                                            label="Interaction Type"
                                            selectedKey={values.completionInteractionType}
                                            options={interactionTypeOptions}
                                            onChange={(e, option) => {
                                                setFieldValue('completionInteractionType', option.key);
                                                setFieldValue('completionInteractionProperty', null);
                                                setFieldValue('completionTargetSlug', '');
                                            }}
                                            styles={{ dropdown: { width: 300 } }}
                                            className="mt10"
                                            required
                                            errorMessage={errors.completionInteractionType}
                                        />
                                        
                                        {values.completionInteractionType && 
                                         interactionTypeDefinitions[values.completionInteractionType]?.length > 0 && (
                                            <>
                                                <Dropdown
                                                    label="Match Property"
                                                    selectedKey={values.completionInteractionProperty}
                                                    options={interactionTypeDefinitions[values.completionInteractionType].map(prop => ({
                                                        key: prop,
                                                        text: prop,
                                                    }))}
                                                    onChange={(e, option) => setFieldValue('completionInteractionProperty', option.key)}
                                                    styles={{ dropdown: { width: 300 } }}
                                                    className="mt10"
                                                    placeholder="Select property to match (optional)"
                                                />
                                                {values.completionInteractionProperty && (
                                                    <TextField
                                                        label="Target Value"
                                                        name="completionTargetSlug"
                                                        className="mt10"
                                                        errorMessage={errors.completionTargetSlug}
                                                        placeholder="Value to match"
                                                    />
                                                )}
                                            </>
                                        )}

                                        {values.completionInteractionType && 
                                         interactionTypeDefinitions[values.completionInteractionType]?.length === 0 && (
                                            <div className="mt10" style={{ fontStyle: 'italic', color: '#666' }}>
                                                Task will be marked complete when the user has any {values.completionInteractionType} interaction.
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        <PrimaryButton
                            className="mt20"
                            onClick={() => handleSubmit()}
                            text={idFromParams ? 'Update' : 'Create'}
                            disabled={submitting}
                        />

                        <Toast show={showToast} message="Saved!" onClose={() => setShowToast(false)} />
                        <FeatureSummary
                            label="About Custom Dashboard Tasks"
                            summary="Create custom dashboard tasks for specific cobrands or cobrand tags. For cobrand-targeted tasks, you can optionally limit visibility to specific reporting units. Configure task properties including slug, category, title, URL, priority, and point values. Tasks can optionally have completion checks based on checklist completion or user interactions like viewing articles, completing courses, or watching videos."
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default WizardPage;
